"use client";

import clsx from "clsx";
import {
  Strategy,
  useStrategiesStore,
  getEffectiveSortField,
  SortField,
} from "@/stores/strategiesStore";
import { MarketViewBadge } from "./StrategyCard/MarketViewBadge";
import { OwnerBadge } from "./StrategyCard/OwnerBadge";
import { PriceBadge } from "./StrategyCard/PriceBadge";
import { RiskBadge } from "./StrategyCard/RiskBadge";
import { AssetClassBadge } from "./AssetClassBadge";
import { ContractBadge } from "./ContractBadge";
import { formatReturn, formatCurrencyCompact } from "@/utils/format";
import { SHOW_STRATEGY_PERFORMANCE } from "@/utils/constants";

interface StrategiesTableViewProps {
  strategies: Strategy[];
  onAllocate: (strategyId: string) => void;
  onManage: (strategyId: string) => void;
  isViewOnly?: boolean;
}

// Issue #131: return/AUM columns are disputed pending an exporter fix +
// restatement — hidden from the catalog table until SHOW_STRATEGY_PERFORMANCE
// flips back to true.
const SORTABLE_COLUMNS: { field: SortField; label: string }[] = SHOW_STRATEGY_PERFORMANCE
  ? [
      { field: "name", label: "Name" },
      { field: "monthReturn", label: "1M" },
      { field: "yearReturn", label: "1Y" },
      { field: "lifetimeReturn", label: "Lifetime" },
      { field: "totalAum", label: "AUM" },
    ]
  : [{ field: "name", label: "Name" }];

export const StrategiesTableView = ({
  strategies,
  onAllocate,
  onManage,
  isViewOnly = false,
}: StrategiesTableViewProps) => {
  const { sortField, sortOrder, setSortField, setSortOrder } = useStrategiesStore();

  // Issue #131 / Codex P2: derive from the same effective field as
  // StrategiesFilterBar's <select> and Strategies.tsx's actual sort
  // application — a stale persisted sortField (e.g. "totalAum" from a prior
  // session) must not desync the header icon from what's actually applied.
  const effectiveSortField = getEffectiveSortField(sortField);

  const handleSort = (field: SortField) => {
    if (effectiveSortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (effectiveSortField !== field)
      return <span className="ml-1 text-slate-600">&#8597;</span>;
    return (
      <span className="ml-1 text-blue-400">
        {sortOrder === "asc" ? "\u2191" : "\u2193"}
      </span>
    );
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white/5">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700/50 text-left text-xs text-slate-400">
            {SORTABLE_COLUMNS.map((col) => (
              <th
                key={col.field}
                className="cursor-pointer px-4 py-3 font-medium transition hover:text-slate-200"
                onClick={() => handleSort(col.field)}
              >
                {col.label}
                <SortIcon field={col.field} />
              </th>
            ))}
            <th className="px-4 py-3 font-medium">Price</th>
            <th className="px-4 py-3 font-medium">Contracts</th>
            <th className="px-4 py-3 font-medium">Asset Class</th>
            <th className="px-4 py-3 font-medium">View</th>
            <th className="px-4 py-3 font-medium">Risk</th>
            <th className="px-4 py-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/30">
          {strategies.map((s) => {
            const hasAllocation = s.userAllocation !== null;
            return (
              <tr key={s.id} className="transition hover:bg-white/5">
                <td className="px-4 py-3 font-medium text-white">
                  <div className="flex items-center gap-2">
                    <span>{s.name}</span>
                    {s.ownerType === "third_party" && <OwnerBadge />}
                  </div>
                </td>
                {SHOW_STRATEGY_PERFORMANCE && (
                  <>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "font-medium",
                          s.monthReturn >= 0 ? "text-emerald-400" : "text-rose-400"
                        )}
                      >
                        {formatReturn(s.monthReturn)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "font-medium",
                          s.yearReturn >= 0 ? "text-emerald-400" : "text-rose-400"
                        )}
                      >
                        {formatReturn(s.yearReturn)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          "font-medium",
                          s.lifetimeReturn >= 0 ? "text-emerald-400" : "text-rose-400"
                        )}
                      >
                        {formatReturn(s.lifetimeReturn)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {formatCurrencyCompact(s.totalAum)}
                    </td>
                  </>
                )}
                <td className="px-4 py-3">
                  <PriceBadge resolvedPrice={s.resolvedPrice} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(s.supportedAssets ?? []).map((asset) => (
                      <ContractBadge key={asset} asset={asset} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(s.assetClasses ?? []).map((ac) => (
                      <AssetClassBadge key={ac} assetClass={ac} />
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <MarketViewBadge view={s.marketView} />
                </td>
                <td className="px-4 py-3">
                  <RiskBadge level={s.riskLevel} />
                </td>
                <td className="px-4 py-3 text-right">
                  {isViewOnly ? (
                    <span className="text-xs text-slate-500">View only</span>
                  ) : hasAllocation ? (
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onManage(s.id)}
                        className="rounded-lg border border-slate-600 px-3 py-1 text-xs font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => onAllocate(s.id)}
                        className="rounded-lg bg-blue-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-blue-600"
                      >
                        + Add
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => onAllocate(s.id)}
                      className="rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-600"
                    >
                      Allocate
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
